/**
 * The hourly forecast-accuracy series for one operating day.
 *
 * Reads accuracy_history/{day}, written once an hour by the accuracy rollup
 * (accuracy/firestore_sync.py in aggregator-trainstate).
 *
 * THE ONE THING TO UNDERSTAND
 * ---------------------------
 * Each stored snapshot is CUMULATIVE day-to-date, not a slice of that hour.
 * Plotting them raw gives a running average, and running averages converge by
 * construction — each new hour is a shrinking fraction of the total. The curve
 * settles by mid-afternoon, which reads as "forecasts improve through the day"
 * when it is really a picture of n growing.
 *
 * So this hook DIFFERENCES consecutive snapshots:
 *
 *     hits_t    = round(n_t * class_hit_rate_t)
 *     n_hour    = n_t - n_{t-1}
 *     rate_hour = (hits_t - hits_{t-1}) / n_hour
 *
 * class_hit_rate is rounded to 4dp by the service, so with n in the thousands
 * round(n * rate) recovers the integer hit count exactly.
 *
 * Only three metrics can be differenced, because only their denominators are
 * returned: class_hit_rate (over n), model_class_hit_rate (over n_model) and
 * high_to_low_miss_rate (over n_high_actual). mae_persons and
 * model_high_to_low_miss_rate cannot — the service computes their denominators
 * but does not expose them — so they stay cumulative-only and must be labelled
 * as such wherever they are shown.
 *
 * WHAT AN HOURLY POINT MEANS: a sample resolves when the train departs the
 * TARGET stop, so a point is "forecasts that RESOLVED in this clock hour" — not
 * forecasts issued in it, nor journeys running in it. An N+5 issued at 13:00
 * lands in a later bucket than an N+1 issued at 13:00.
 *
 * Requires the accuracy_history/{day} rule in firestore.rules. Until deployed,
 * writes succeed while reads are denied — which looks like a broken listener
 * rather than a missing rule.
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase';
import type { AccuracyDay, AccuracyNodeDoc } from './useHomeLive';

/** One stored snapshot. Cumulative — see the header. */
export type AccuracySnapshot = Pick<AccuracyDay,
  'day' | 'n_samples' | 'n_journeys' | 'journey_fetch_errors'
  | 'model_source' | 'model_class_coverage'> & {
  overall?: AccuracyNodeDoc | null;
  by_line?: Record<string, AccuracyNodeDoc> | null;
};

export type AccuracyHistoryDoc = {
  day?: string | null;
  /** Keyed "00".."23". An explicit null is a failed hour, and is not a gap. */
  snapshots?: Record<string, AccuracySnapshot | null> | null;
};

/** One point on the chart: the hour's own figures, not the running total. */
export type HourlyPoint = {
  /** "14" — the hour the snapshot was taken, Europe/Berlin. */
  hour: string;
  /** Samples that resolved in THIS hour. */
  nHour: number;
  /** Day-to-date, for context in the tooltip. */
  nCumulative: number;
  /** Message-carried class hit rate for this hour alone (0..1), or null. */
  hitRate: number | null;
  /** Forecaster-delivered class hit rate for this hour alone (0..1), or null. */
  modelHitRate: number | null;
  /** HOCH read as NIEDRIG, this hour alone (0..1), or null. */
  highToLowRate: number | null;
  /** Cumulative only — cannot be differenced, see the header. */
  maeCumulative: number | null;
};

const cell = (s: AccuracySnapshot | null | undefined) => s?.overall?.all;

/** round(n * rate) recovers the exact integer count — see the header. */
function counted(n: number | null | undefined, rate: number | null | undefined): number | null {
  if (typeof n !== 'number' || typeof rate !== 'number') return null;
  return Math.round(n * rate);
}

function rateBetween(
  nPrev: number | null | undefined, ratePrev: number | null | undefined,
  nNow: number | null | undefined, rateNow: number | null | undefined,
): { n: number; rate: number | null } | null {
  if (typeof nNow !== 'number') return null;
  const prevN = typeof nPrev === 'number' ? nPrev : 0;
  const dn = nNow - prevN;
  // A non-positive delta means the counts did not advance (a repeated snapshot,
  // or a recomputation that landed lower). Not a zero-accuracy hour — no point.
  if (dn <= 0) return null;
  const hitsNow = counted(nNow, rateNow);
  const hitsPrev = prevN === 0 ? 0 : counted(nPrev, ratePrev);
  if (hitsNow === null || hitsPrev === null) return { n: dn, rate: null };
  return { n: dn, rate: (hitsNow - hitsPrev) / dn };
}

/**
 * Difference the cumulative snapshots into per-hour points.
 *
 * Exported so it can be unit-tested without Firestore — the arithmetic here is
 * the whole value of the chart, and getting it wrong produces a plausible-
 * looking curve rather than an obvious error.
 */
export function toHourlyPoints(
  snapshots: Record<string, AccuracySnapshot | null> | null | undefined,
): HourlyPoint[] {
  if (!snapshots) return [];
  const hours = Object.keys(snapshots).filter(h => snapshots[h]).sort();
  const points: HourlyPoint[] = [];

  let prev: AccuracySnapshot | null = null;
  for (const hour of hours) {
    const now = snapshots[hour]!;
    const c = cell(now);
    const p = cell(prev);

    const displayed = rateBetween(p?.n, p?.class_hit_rate, c?.n, c?.class_hit_rate);
    if (displayed) {
      const model = rateBetween(p?.n_model, p?.model_class_hit_rate,
                                c?.n_model, c?.model_class_hit_rate);
      const high = rateBetween(p?.n_high_actual, p?.high_to_low_miss_rate,
                               c?.n_high_actual, c?.high_to_low_miss_rate);
      points.push({
        hour,
        nHour: displayed.n,
        nCumulative: typeof c?.n === 'number' ? c.n : 0,
        hitRate: displayed.rate,
        modelHitRate: model ? model.rate : null,
        highToLowRate: high ? high.rate : null,
        maeCumulative: typeof c?.mae_persons === 'number' ? c.mae_persons : null,
      });
    }
    prev = now;
  }
  return points;
}

export type AccuracyHistory = {
  points: HourlyPoint[];
  /** The newest cumulative snapshot, for a day-to-date figure beside the chart. */
  latest: AccuracySnapshot | null;
  loaded: boolean;
  error: Error | null;
};

/**
 * @param day operating day, YYYY-MM-DD. Pass null to listen to nothing.
 */
export function useAccuracyHistory(day: string | null): AccuracyHistory {
  const [snap, setSnap] = useState<AccuracyHistoryDoc | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!day) { setSnap(null); setLoaded(true); return; }
    setLoaded(false);
    const unsub = onSnapshot(
      doc(db, 'accuracy_history', day),
      (s) => {
        setSnap(s.exists() ? (s.data() as AccuracyHistoryDoc) : null);
        setLoaded(true);
        setError(null);
      },
      (err) => {
        // permission-denied here almost always means the accuracy_history rule
        // has not been deployed, not that the writer has stopped.
        console.error('❌ accuracy_history onSnapshot error', err);
        setError(err as Error);
        setLoaded(true);
      },
    );
    return unsub;
  }, [day]);

  const points = useMemo(() => toHourlyPoints(snap?.snapshots), [snap]);
  const latest = useMemo(() => {
    const hours = Object.keys(snap?.snapshots ?? {}).filter(h => snap!.snapshots![h]).sort();
    return hours.length ? snap!.snapshots![hours[hours.length - 1]]! : null;
  }, [snap]);

  return { points, latest, loaded, error };
}
